import { useState, useRef } from "react";
import { API_BASE, formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, ArrowRight, Check, AlertCircle, RefreshCw } from "lucide-react";

const ENTITIES = [
  {
    id: "income",
    label: "Income",
    desc: "Logs paid invoices, YouTube / Stripe / Razorpay payouts and any other receipts.",
    accent: "#4a6b53",
  },
  {
    id: "expense",
    label: "Expenses",
    desc: "Subscriptions, travel, equipment — track GST-input-credit-eligible spends.",
    accent: "#d4b872",
  },
  {
    id: "client",
    label: "Clients",
    desc: "Your contact book — names, GSTINs, addresses for invoice headers.",
    accent: "#3d5a8a",
  },
  {
    id: "bank",
    label: "Bank statement",
    desc: "Auto-create incomes (credits) and expenses (debits) from any bank CSV/Excel.",
    accent: "#d95d39",
  },
];

export default function Import() {
  const [active, setActive] = useState("income");

  return (
    <div className="space-y-8 max-w-4xl" data-testid="import-page">
      <div>
        <p className="tc-overline mb-2">Bulk import</p>
        <h1 className="font-['Outfit'] text-3xl md:text-4xl font-bold tracking-tight">Bring your data in</h1>
        <p className="text-sm text-[#5a5a53] mt-2 max-w-2xl">
          Upload an Excel (.xlsx) or Comma-Separated Values (.csv) file. We'll auto-detect your columns,
          show a preview, and let you tweak the mapping before creating entries. Max 5 MB / 2,000 rows per file.
        </p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="import-tabs">
        {ENTITIES.map((e) => (
          <button
            key={e.id}
            onClick={() => setActive(e.id)}
            data-testid={`import-tab-${e.id}`}
            className={`px-4 py-2 text-sm rounded-md border transition-colors ${
              active === e.id
                ? "bg-[#0a0a0a] text-white border-[#0a0a0a]"
                : "bg-white border-[#e5e5e0] text-[#5a5a53] hover:bg-[#f4f1eb]"
            }`}
          >
            {e.label}
          </button>
        ))}
      </div>

      <ImportPanel key={active} entity={ENTITIES.find((e) => e.id === active)} />
    </div>
  );
}

function ImportPanel({ entity }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setPreview(null); setMapping({}); setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const onFile = async (f) => {
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setFile(f); setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("entity_type", entity.id);
      const r = await fetch(`${API_BASE}/imports/preview`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail) || "Preview failed");
      setPreview(data);
      setMapping(data.suggested_mapping || {});
    } catch (e) {
      toast.error(e.message);
      reset();
    } finally { setLoading(false); }
  };

  const doImport = async () => {
    // Verify required mappings
    const missing = Object.entries(preview.targets)
      .filter(([_, def]) => def.required)
      .filter(([t]) => !mapping[t])
      .map(([t]) => t);
    // For bank, require date+description AND at least one of credit/debit/amount
    if (entity.id === "bank") {
      const hasMoney = mapping.credit_amount || mapping.debit_amount || mapping.amount;
      if (!hasMoney) {
        toast.error("Map at least one of: credit, debit, or signed amount column");
        return;
      }
    }
    if (missing.length > 0) {
      toast.error(`Required fields not mapped: ${missing.join(", ")}`);
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("entity_type", entity.id);
      fd.append("mapping", JSON.stringify(mapping));
      const r = await fetch(`${API_BASE}/imports/execute`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      const data = await r.json();
      if (!r.ok) throw new Error(formatApiErrorDetail(data.detail) || "Import failed");
      setResult(data);
      const msg = entity.id === "bank"
        ? `Imported ${data.incomes_created} income(s) + ${data.expenses_created} expense(s)`
        : `Imported ${data.imported} ${entity.label.toLowerCase()}`;
      toast.success(msg);
    } catch (e) {
      toast.error(e.message);
    } finally { setLoading(false); }
  };

  if (result) {
    return (
      <div className="tc-card p-8 space-y-4" data-testid="import-success">
        <div className="flex items-start gap-3">
          <Check size={20} className="text-[#4a6b53] mt-1 shrink-0" />
          <div>
            <p className="tc-overline" style={{ color: "#4a6b53" }}>Import complete</p>
            <h2 className="font-['Outfit'] text-2xl font-medium tracking-tight mt-1">
              {entity.id === "bank"
                ? `${result.incomes_created} credits and ${result.expenses_created} debits saved`
                : `${result.imported} ${entity.label.toLowerCase()} created`}
            </h2>
            {result.error_count > 0 && (
              <p className="mt-2 text-sm text-[#b83a26]">{result.error_count} row(s) had errors and were skipped.</p>
            )}
          </div>
        </div>
        {result.errors && result.errors.length > 0 && (
          <div className="border border-[#f5c6b9] bg-[#fff4f1] rounded-md p-4 text-xs space-y-1 max-h-48 overflow-y-auto">
            <p className="font-bold text-[#b83a26] mb-2">Errors ({result.errors.length} shown):</p>
            {result.errors.map((e, i) => (
              <div key={i} className="font-mono text-[#5a5a53]">
                Row {e.row}: <span className="text-[#b83a26]">{e.error}</span>
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-3 pt-2">
          <Button onClick={reset} variant="outline" className="border-[#e5e5e0]" data-testid="import-another-btn">
            <RefreshCw size={14} className="mr-2" /> Import another file
          </Button>
        </div>
      </div>
    );
  }

  if (!preview) {
    return (
      <div className="tc-card p-8 space-y-6">
        <div>
          <p className="tc-overline" style={{ color: entity.accent }}>{entity.label}</p>
          <p className="text-sm text-[#5a5a53] mt-2">{entity.desc}</p>
        </div>
        <label
          htmlFor="import-file-input"
          className="border-2 border-dashed border-[#e5e5e0] rounded-md p-12 text-center cursor-pointer hover:bg-[#fafaf8] hover:border-[#d95d39] transition-colors block"
          data-testid="import-drop-zone"
        >
          <Upload size={32} className="mx-auto text-[#5a5a53]" />
          <p className="mt-4 text-sm font-medium text-[#0a0a0a]">
            {loading ? "Reading file…" : "Click to choose .xlsx or .csv"}
          </p>
          <p className="mt-1 text-xs text-[#a3a39b]">Max 5 MB · We'll auto-detect columns</p>
        </label>
        <input
          ref={inputRef}
          id="import-file-input"
          type="file"
          accept=".csv,.xlsx,.xlsm"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="import-file-input"
          disabled={loading}
        />
        <ExpectedColumns entity={entity} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="import-mapping-step">
      <div className="tc-card p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <FileSpreadsheet size={20} className="text-[#d95d39] shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium text-[#0a0a0a] truncate">{file?.name}</div>
              <div className="text-xs text-[#a3a39b]">{preview.row_count} data rows · {preview.headers.length} columns</div>
            </div>
          </div>
          <button onClick={reset} className="text-xs text-[#5a5a53] hover:text-[#b83a26] uppercase tracking-wider font-bold" data-testid="import-reset-btn">
            Pick another file
          </button>
        </div>
      </div>

      <div className="tc-card p-6 space-y-4">
        <div>
          <p className="tc-overline" style={{ color: entity.accent }}>Map columns</p>
          <p className="text-sm text-[#5a5a53] mt-2">
            Match each field on the left to a column from your file. We've pre-filled the obvious ones — adjust where needed.
          </p>
        </div>
        <div className="space-y-3">
          {Object.entries(preview.targets).map(([target, def]) => (
            <div key={target} className="grid grid-cols-1 md:grid-cols-[180px_1fr_1fr] gap-3 items-center">
              <Label className="text-xs uppercase tracking-[0.18em] font-bold text-[#5a5a53]">
                {target.replace(/_/g, " ")} {def.required && <span className="text-[#b83a26]">*</span>}
              </Label>
              <Select
                value={mapping[target] || "__none__"}
                onValueChange={(v) => setMapping({ ...mapping, [target]: v === "__none__" ? null : v })}
              >
                <SelectTrigger className="h-10 rounded-md border-[#e5e5e0]" data-testid={`import-map-${target}`}>
                  <SelectValue placeholder="Not mapped" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Not mapped —</SelectItem>
                  {preview.headers.map((h) => (
                    <SelectItem key={h} value={h}>{h}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-[#a3a39b] italic">{def.hint}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Preview rows */}
      <div className="tc-card overflow-hidden overflow-x-auto">
        <div className="p-4 border-b border-[#e5e5e0]">
          <p className="tc-overline">First {preview.sample_rows.length} rows</p>
        </div>
        <table className="w-full text-xs min-w-[640px]">
          <thead className="bg-[#f4f1eb] text-[10px] tracking-[0.18em] uppercase text-[#5a5a53]">
            <tr>
              {preview.headers.map((h) => (
                <th key={h} className="text-left px-4 py-2 font-bold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.sample_rows.map((r, i) => (
              <tr key={i} className="border-t border-[#e5e5e0]">
                {preview.headers.map((h) => (
                  <td key={h} className="px-4 py-2 font-mono">{r[h] || "—"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-3">
        <Button onClick={reset} variant="outline" className="border-[#e5e5e0]" data-testid="import-cancel-btn">
          Cancel
        </Button>
        <Button
          onClick={doImport}
          disabled={loading}
          className="bg-[#d95d39] hover:bg-[#c84c35] text-white"
          data-testid="import-execute-btn"
        >
          {loading ? "Importing…" : <>Import {preview.row_count} row(s) <ArrowRight size={14} className="ml-2" /></>}
        </Button>
      </div>
    </div>
  );
}

function ExpectedColumns({ entity }) {
  const cols = {
    income:  ["date", "source", "amount", "currency (opt)", "fx_rate (opt)", "gst_applicable (opt)", "gst_rate (opt)", "notes (opt)"],
    expense: ["date", "vendor", "category", "amount", "gst_amount (opt)", "is_input_credit (opt)", "notes (opt)"],
    client:  ["name", "email (opt)", "phone (opt)", "company (opt)", "gstin (opt)", "address (opt)"],
    bank:    ["date", "description", "credit_amount + debit_amount (separate columns)", "OR amount + type (CR/DR)"],
  }[entity.id] || [];
  const notes = {
    income:  "Source must be one of: YouTube, Stripe, Razorpay, PayPal, Wise, Patreon, Bank credit, Other, etc. Anything else becomes 'Other'.",
    expense: "GST component goes in gst_amount, NOT in amount. is_input_credit = true / 1 / yes to claim Input Tax Credit (ITC).",
    client:  "Just name is required. GSTIN is the 15-digit number for B2B invoices.",
    bank:    "Date formats accepted: DD-MM-YYYY, DD/MM/YYYY, YYYY-MM-DD, DD-MMM-YYYY. Amounts can be plain numbers, '1,23,456.78', or '50 Cr' / '50 Dr'.",
  }[entity.id];
  return (
    <div className="text-xs text-[#5a5a53] border-t border-[#e5e5e0] pt-4">
      <p className="tc-overline mb-2">Expected columns</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {cols.map((c) => (
          <code key={c} className="px-2 py-0.5 bg-[#f4f1eb] rounded text-[10px] font-mono">{c}</code>
        ))}
      </div>
      <p className="text-[11px] text-[#a3a39b] italic">{notes}</p>
    </div>
  );
}
